import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { playJSONDownloadSound } from "../utils/soundUtils";

const getFormVersion = () =>
  `${new Date().getFullYear().toString().slice(-2)}.0.0`;

const buildFormRules = (formRules = { formNotes: "", fields: [] }, formId = "00000000-0000-0000-0000-000000000000") => {
  return {
    form: formId,
    version: getFormVersion(),
    formNotes: (formRules.formNotes || "").trim() || null,
    fields: (formRules.fields || []).map((entry) => ({
      attributeName: entry.attributeName,
      required: entry.rules?.required || false,
      notes: entry.rules?.notes || "",
      sectionNumber: entry.rules?.sectionNumber || "",
      sectionName: entry.rules?.sectionName || "",
      sectionDescription: entry.rules?.sectionDescription || "",
      sectionNotes: entry.rules?.sectionNotes || "",
      pdfLocation: entry.pdfLocation || null,
      options: (entry.options || []).map((opt) => ({
        kind: opt.kind || "option",
        value: opt.value || "",
        pdfLocation: opt.pdfLocation || null,
        ...(opt.kind !== "option" ? { other: true } : {}),
        ...(opt.kind === "option" && opt.index != null ? { index: opt.index } : {}),
      })),
    })),
  };
};

export const generateRulesJson = (formRules, formId) =>
  JSON.stringify(buildFormRules(formRules, formId), null, 2);

const renderPrettyContent = (rules, { hideEmpty = false } = {}) => (
  <Stack spacing={3}>
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Form Summary
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        <Typography variant="body2">Form ID: {rules.form}</Typography>
        <Typography variant="body2">Version: {rules.version}</Typography>
        {!hideEmpty || rules.formNotes ? (
          <Typography variant="body2">
            Form Notes: {rules.formNotes}
          </Typography>
        ) : null}
      </Stack>
    </Box>
    <Divider />
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Fields
      </Typography>
      <Stack spacing={2}>
        {rules.fields.map((rule, index) => {
          const optionCount = rule.options?.length || 0;
          return (
            <Box
              key={`${rule.attributeName || "field"}-${index}`}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {index + 1}. {rule.attributeName || "Untitled Field"}
              </Typography>
              <Stack spacing={0.5}>
                {!hideEmpty || rule.required ? (
                  <Typography variant="body2">Required: Yes</Typography>
                ) : null}
                {!hideEmpty || rule.notes ? (
                  <Typography variant="body2">Notes: {rule.notes}</Typography>
                ) : null}
                {!hideEmpty || rule.sectionNumber ? (
                  <Typography variant="body2">
                    Section Number: {rule.sectionNumber}
                  </Typography>
                ) : null}
                {!hideEmpty || rule.sectionName ? (
                  <Typography variant="body2">
                    Section Name: {rule.sectionName}
                  </Typography>
                ) : null}
                {!hideEmpty || rule.sectionDescription ? (
                  <Typography variant="body2">
                    Section Description: {rule.sectionDescription}
                  </Typography>
                ) : null}
                {!hideEmpty || rule.sectionNotes ? (
                  <Typography variant="body2">
                    Section Notes: {rule.sectionNotes}
                  </Typography>
                ) : null}
                {!hideEmpty || rule.pdfLocation ? (
                  <Typography variant="body2">PDF Location: Included</Typography>
                ) : null}
                {optionCount > 0 ? (
                  <Typography variant="body2">
                    Options: {optionCount}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  </Stack>
);

const syntaxHighlight = (json) => {
  if (!json) return "";

  const escaped = json
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      function (match) {
        let style = "color: #b5cea8;"; // Numbers

        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            style = "color: #9cdcfe;"; // Property names
          } else {
            style = "color: #ce9178;"; // Strings
          }
        } else if (/true|false/.test(match)) {
          style = "color: #569cd6;"; // Booleans
        } else if (/null/.test(match)) {
          style = "color: #569cd6;"; // Null
        }

        return `<span style="${style}">${match}</span>`;
      },
    )
    .replace(
      /[{}\[\]]/g,
      (match) => `<span style="color: #d4d4d4;">${match}</span>`,
    )
    .replace(/,/g, '<span style="color: #d4d4d4;">,</span>');
};

const FormRulesPreview = ({
  formRules = { formNotes: "", fields: [] },
  formId = "00000000-0000-0000-0000-000000000000",
  mode = "json",
  onClose,
}) => {
  const rules = buildFormRules(formRules, formId);
  const isPretty = mode === "pretty" || mode === "annotator";
  const hideEmpty = mode === "annotator";
  const handleDownload = () => {
    const json = generateRulesJson(formRules, formId);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "form-rules.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    playJSONDownloadSound();
  };

  const renderContent = generateRulesJson(formRules, formId);

  return (
    <Card>
      <CardHeader
        title="Form Rules Preview"
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={handleDownload} size="small">
              Download Rules
            </Button>
            {onClose && (
              <IconButton onClick={onClose} size="small" aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      />
      <CardContent>
        {isPretty ? (
          renderPrettyContent(rules, { hideEmpty })
        ) : (
          <pre
            style={{
              backgroundColor: "#1e1e1e",
              color: "#d4d4d4",
              padding: "1rem",
              borderRadius: "4px",
              overflow: "auto",
              fontSize: "14px",
              fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: syntaxHighlight(renderContent),
              }}
            />
          </pre>
        )}
      </CardContent>
    </Card>
  );
};

export default FormRulesPreview;
export { buildFormRules };
